import { message } from 'antd'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde'
import {
  CalibrationPageData,
  calibrationPageDataSchema,
  ClientProvidedImagesData,
  clientProvidedImagesDataSchema,
  CoverPageData,
  coverPageDataSchema,
  MainInfo,
  mainInfoSchema,
  RautFormDataState,
  SummaryPageData,
  summaryPageDataSchema,
  WeldScanData,
  weldScanDataSchema,
} from 'pages/reporting/reports/raut/rautReportData'
import { getIndicationKey } from 'pages/reporting/reports/raut/utils/indicationUtils'
import {
  AssetDrawingsData,
  RevisionMetadata,
  SavedIndicationGraph,
} from 'pages/reporting/shared/types'
import { convertDatesToDay } from 'pages/reporting/shared/utils/dateUtils'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoReportingMetadata,
  LockState,
  SignatureData,
  SignoffState,
} from 'types'

export function deserializeGeckoMeasurementsAndDates<T>(data: T): T {
  const isGeckoMeasurementJson = (
    obj: unknown
  ): obj is GeckoMeasurementJson => {
    if (obj === null || typeof obj !== 'object') return false

    // Check if the object has 'num' and 'unit' properties
    if (!('num' in obj) || !('unit' in obj)) return false

    // Get the object with the proper type to access properties
    const candidate = obj as Record<string, unknown>

    // Check if num is a number and unit is a string
    return (
      typeof candidate.num === 'number' && typeof candidate.unit === 'string'
    )
  }

  if (data === null || data === undefined) return data

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) =>
      deserializeGeckoMeasurementsAndDates(item)
    ) as unknown as T
  }

  if (typeof data === 'string') {
    // Dayjs can't validate the UTC indicator Z,
    // so check for it, then remove it and try ISO8601 validating
    if (data.includes('Z')) {
      if (
        dayjs(
          data.slice(0, data.length - 1),
          'YYYY-MM-DDTHH:mm:ss.SSS',
          true
        ).isValid()
      ) {
        return dayjs(data) as unknown as T
      }
    }
  }

  // Handle objects
  if (typeof data === 'object') {
    // Check if this looks like a serialized GeckoMeasurement
    if (isGeckoMeasurementJson(data)) {
      // Recreate a proper GeckoMeasurement object
      return GeckoMeasurement.CreateFromJSON({
        num: data.num,
        unit: data.unit,
      }) as unknown as T
    }

    // Process each property recursively
    const result: Record<string, unknown> = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = deserializeGeckoMeasurementsAndDates(
          (data as Record<string, unknown>)[key]
        )
      }
    }
    return result as unknown as T
  }

  return data
}

export function getSelectedFormData(
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[]
) {
  const currentEntry = metadataEntries.find(
    (entry) => entry.id === metadataEntryId
  )

  if (!currentEntry) {
    console.warn('Current metadata entry not found')
    return
  }

  const existingData = currentEntry.data as Record<string, unknown>

  // Deserialize the data, properly reconstructing GeckoMeasurement objects
  const deserializedEntry = deserializeGeckoMeasurementsAndDates(existingData)
  const deserializedData = deserializedEntry['section_info'] as Record<
    string,
    unknown
  >

  const newFormData = {
    mainInfo: (deserializedData.main_info as MainInfo) || ({} as MainInfo),
    coverPageData: convertDatesToDay(
      (deserializedData.cover_page || {}) as Record<string, unknown>
    ) as unknown as CoverPageData,
    summaryPageData:
      (deserializedData.summary_page as SummaryPageData) ||
      ({} as SummaryPageData),
    calibrationData: convertDatesToDay(
      (deserializedData.calibration_page || {}) as Record<string, unknown>
    ) as unknown as CalibrationPageData,
    clientProvidedImagesData:
      (deserializedData.client_provided_images as ClientProvidedImagesData) ||
      ({} as ClientProvidedImagesData),
    asset_drawings:
      (deserializedData.asset_drawings as RautFormDataState['asset_drawings']) || {
        images: {},
      },
    savedIndicationGraphs:
      (deserializedData.savedIndicationGraphs as Record<
        string,
        SavedIndicationGraph
      >) || ({} as Record<string, SavedIndicationGraph>),
    savedWeldIndicationGraphs:
      (deserializedData.savedWeldIndicationGraphs as RautFormDataState['savedWeldIndicationGraphs']) ||
      undefined,
    weldScanData: (() => {
      return (
        (deserializedEntry.weld_scan_data as RautFormDataState['weldScanData']) ||
        undefined
      )
    })(),
    signoff: (deserializedEntry.signoff as SignoffState) || {
      lastRevision: 0,
    },
    revisionMetadata:
      (deserializedEntry.revisionMetadata as RevisionMetadata) || {
        revisionNumber: 1,
        createdAt: new Date().toISOString(),
        isPublished: false,
      },
    lockState: (deserializedEntry.lockState as LockState) || {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: (deserializedEntry.publishedPdfPath as string) || '',
  }

  return newFormData
}

export const useFormData = (
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  isLoading: boolean
) => {
  const [formData, setFormData] = useState<RautFormDataState>({
    mainInfo: {} as MainInfo,
    coverPageData: {} as CoverPageData,
    summaryPageData: {} as SummaryPageData,
    calibrationData: {} as CalibrationPageData,
    clientProvidedImagesData: {} as ClientProvidedImagesData,
    asset_drawings: {
      images: {},
    },
    weldScanData: undefined,
    signoff: {
      lastRevision: 0,
    },
    revisionMetadata: {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    lockState: {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: '',
  })

  const { rolodexPatch } = useRolodexEdit()

  const lastSyncedEntryRef = useRef<{ id: string; generation: number } | null>(
    null
  )

  const patchSection = useCallback(
    async (section: string, sectionData: Record<string, unknown>) => {
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: {
            section_info: {
              [section]: sectionData,
            },
          },
        },
      ])
    },
    [metadataEntryId, rolodexPatch]
  )

  type SerializableValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | SerializableObject
    | SerializableArray

  interface SerializableObject {
    [key: string]: SerializableValue
  }

  type SerializableArray = SerializableValue[]

  useEffect(() => {
    if (isLoading) return

    const currentEntry = metadataEntries.find(
      (entry) => entry.id === metadataEntryId
    )
    if (!currentEntry) return

    if (
      lastSyncedEntryRef.current?.id === metadataEntryId &&
      lastSyncedEntryRef.current?.generation === currentEntry.generation
    ) {
      return
    }

    const newFormData = getSelectedFormData(metadataEntryId, metadataEntries)
    if (newFormData) {
      setFormData(newFormData)
      lastSyncedEntryRef.current = {
        id: metadataEntryId,
        generation: currentEntry.generation,
      }
    }
  }, [isLoading, metadataEntries, metadataEntryId])

  const updateFormData = useCallback((newData: Partial<RautFormDataState>) => {
    setFormData((prev) => ({ ...prev, ...newData }))
  }, [])

  const handleMainInfoFinish = useCallback(
    async (value: MainInfo) => {
      try {
        const merged = { ...formData.mainInfo, ...value }
        const parsed = mainInfoSchema.parse(merged)
        updateFormData({ mainInfo: parsed })

        await patchSection('main_info', { ...parsed })
        message.success('Main Info saved successfully!')
      } catch (error) {
        console.error('Failed to save main info:', error)
        message.error('Failed to save main info')
      }
    },
    [formData.mainInfo, patchSection, updateFormData]
  )

  const handleCoverPageFinish = useCallback(
    async (values: CoverPageData) => {
      try {
        const parsed = coverPageDataSchema.parse(values)

        const storageValues = {
          ...parsed,
          startDate: parsed.startDate?.toISOString(),
          endDate: parsed.endDate?.toISOString(),
        }

        updateFormData({
          coverPageData: parsed,
        })

        await patchSection('cover_page', { ...storageValues })

        message.success('Cover page saved successfully!')
      } catch (error) {
        console.error('Failed to save cover page:', error)
        message.error('Failed to save cover page')
      }
    },
    [updateFormData, patchSection]
  )

  const saveSummaryPageSilent = useCallback(
    async (values: SummaryPageData) => {
      const parsed = summaryPageDataSchema.parse(values)

      updateFormData({
        summaryPageData: parsed,
      })

      await patchSection('summary_page', { ...parsed })
    },
    [updateFormData, patchSection]
  )

  const saveMainInfoSilent = useCallback(
    async (value: Partial<MainInfo>) => {
      // Merge with existing mainInfo, providing default for required scanIndicationMode
      const merged = {
        ...formData.mainInfo,
        ...value,
        // Ensure scanIndicationMode has a value (required by schema)
        scanIndicationMode:
          value.scanIndicationMode ??
          formData.mainInfo.scanIndicationMode ??
          false,
      }
      const parsed = mainInfoSchema.parse(merged)
      updateFormData({ mainInfo: parsed })

      await patchSection('main_info', { ...parsed })
    },
    [formData.mainInfo, patchSection, updateFormData]
  )

  const handleSummaryPageFinish = useCallback(
    async (values: SummaryPageData) => {
      try {
        await saveSummaryPageSilent(values)
        message.success('Summary page saved successfully!')
      } catch (error) {
        console.error('Error saving summary page:', error)
        message.error('Failed to save summary page')
      }
    },
    [saveSummaryPageSilent]
  )

  const handleCalibrationFinish = useCallback(
    async (values: Partial<CalibrationPageData>, isComplete: boolean) => {
      try {
        const parsed: Partial<CalibrationPageData> = isComplete
          ? calibrationPageDataSchema.parse(values)
          : values

        const storageValues = {
          ...parsed,
          calibrationVerification: parsed.calibrationVerification?.map(
            (verification) => ({
              ...verification,
              date: verification.date?.toISOString() || null,
            })
          ),
          instrumentCalibrationDate:
            parsed.instrumentCalibrationDate?.toISOString(),
        }
        updateFormData({ calibrationData: parsed as CalibrationPageData })

        await patchSection('calibration_page', storageValues)

        message.success('Calibration page saved successfully')
      } catch (error) {
        console.error('Failed to save calibration page:', error)
        message.error('Failed to save calibration page')
        throw error
      }
    },
    [updateFormData, patchSection]
  )

  const handleClientProvidedImagesFinish = useCallback(
    async (values: ClientProvidedImagesData) => {
      try {
        const parsed = clientProvidedImagesDataSchema.parse(values)

        updateFormData({ clientProvidedImagesData: parsed })

        await patchSection('client_provided_images', parsed)
        message.success('Client provided images saved successfully!')
      } catch (error) {
        console.error('Failed to save client provided images:', error)
        message.error('Failed to save client provided images')
      }
    },
    [updateFormData, patchSection]
  )
  const handleWeldScanDataFinish = useCallback(
    async (values: WeldScanData) => {
      try {
        const parsed = weldScanDataSchema.parse(values)

        updateFormData({ weldScanData: parsed })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              weld_scan_data: parsed,
            },
          },
        ])
        message.success('Weld scan data saved successfully!')
      } catch (error) {
        console.error('Failed to save weld scan data:', error)
        message.error('Failed to save weld scan data')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleAssetDrawingsFinish = useCallback(
    async (
      imagePath: string,
      imageType:
        | 'heatmap'
        | 'no-heatmap'
        | 'heatmap-with-indications'
        | 'welds-no-heatmap',
      index: number,
      inspectionId: string,
      imageCaption?: string
    ) => {
      try {
        const newImage: AssetDrawingsData = {
          [inspectionId]: {
            [imageType]: {
              imagePath,
              imageCaption,
              index: index,
              type: imageType,
            },
          },
        }

        updateFormData({
          asset_drawings: {
            ...newImage,
          },
        })

        await patchSection('asset_drawings', newImage)
        message.success('Asset drawing saved successfully!')
      } catch {
        message.error('Failed to save asset drawing')
      }
    },
    [updateFormData, patchSection]
  )

  const handleSaveIndicationGraph = useCallback(
    async (graphData: SavedIndicationGraph) => {
      try {
        const updatedGraphs = {
          ...(formData.savedIndicationGraphs || {}),
          [graphData.indicationId]: graphData,
        }

        updateFormData({ savedIndicationGraphs: updatedGraphs })

        await patchSection('savedIndicationGraphs', updatedGraphs)

        return updatedGraphs
      } catch (error) {
        console.error('Failed to save indication graph:', error)
        message.error('Failed to save indication graph')
        throw error
      }
    },
    [formData.savedIndicationGraphs, patchSection, updateFormData]
  )

  const handleDeleteIndicationGraph = useCallback(
    async (indicationId: string) => {
      try {
        const updatedGraphs = { ...(formData.savedIndicationGraphs || {}) }
        delete updatedGraphs[indicationId]

        updateFormData({ savedIndicationGraphs: updatedGraphs })

        await rolodexPatch([
          {
            id: metadataEntryId,
            delete_data: [`section_info.savedIndicationGraphs.${indicationId}`],
          },
        ])

        return updatedGraphs
      } catch (error) {
        console.error('Failed to delete indication graph:', error)
        message.error('Failed to delete indication graph')
        throw error
      }
    },
    [
      formData.savedIndicationGraphs,
      metadataEntryId,
      rolodexPatch,
      updateFormData,
    ]
  )

  const handleSaveWeldIndicationGraph = useCallback(
    async (indicationId: string, imagePath: string) => {
      try {
        const key = getIndicationKey(indicationId)
        const updatedGraphs = {
          ...(formData.savedWeldIndicationGraphs || {}),
          [key]: {
            imagePath,
            timestamp: new Date().toISOString(),
          },
        }

        updateFormData({ savedWeldIndicationGraphs: updatedGraphs })

        await patchSection('savedWeldIndicationGraphs', updatedGraphs)

        return updatedGraphs
      } catch (error) {
        console.error('Failed to save weld indication graph:', error)
        message.error('Failed to save weld indication graph')
        throw error
      }
    },
    [formData.savedWeldIndicationGraphs, patchSection, updateFormData]
  )

  const handleDeleteWeldIndicationGraph = useCallback(
    async (indicationId: string) => {
      try {
        const key = getIndicationKey(indicationId)
        const updatedGraphs = {
          ...(formData.savedWeldIndicationGraphs || {}),
        }
        delete updatedGraphs[key]

        updateFormData({ savedWeldIndicationGraphs: updatedGraphs })

        await rolodexPatch([
          {
            id: metadataEntryId,
            delete_data: [`section_info.savedWeldIndicationGraphs.${key}`],
          },
        ])

        return updatedGraphs
      } catch (error) {
        console.error('Failed to delete weld indication graph:', error)
        message.error('Failed to delete weld indication graph')
        throw error
      }
    },
    [
      formData.savedWeldIndicationGraphs,
      metadataEntryId,
      rolodexPatch,
      updateFormData,
    ]
  )

  const handleSignoffFinish = useCallback(
    async (signatureData: SignatureData): Promise<SignoffState> => {
      try {
        const updatedSignoff: SignoffState = {
          ...(formData.signoff || { lastRevision: 0 }),
          [signatureData.role === 'supervisor'
            ? 'supervisorSignoff'
            : 'reviewerSignoff']: signatureData,
        }

        // Increment revision if reviewer is signing
        if (
          signatureData.role === 'reviewer' &&
          updatedSignoff.supervisorSignoff
        ) {
          updatedSignoff.lastRevision = (updatedSignoff.lastRevision || 0) + 1
        }

        // Update Rolodex first
        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              signoff: updatedSignoff,
            },
          },
        ])

        // Then update local state
        updateFormData({
          signoff: updatedSignoff,
        })

        return updatedSignoff
      } catch (error) {
        console.error('Failed to save signoff:', error)
        throw error
      }
    },
    [formData.signoff, metadataEntryId, rolodexPatch, updateFormData]
  )

  return {
    formData,
    updateFormData,
    handleMainInfoFinish,
    handleCoverPageFinish,
    handleSummaryPageFinish,
    saveSummaryPageSilent,
    saveMainInfoSilent,
    handleCalibrationFinish,
    handleClientProvidedImagesFinish,
    handleWeldScanDataFinish,
    handleAssetDrawingsFinish,
    handleSaveIndicationGraph,
    handleDeleteIndicationGraph,
    handleSaveWeldIndicationGraph,
    handleDeleteWeldIndicationGraph,
    handleSignoffFinish,
  }
}
